<script setup lang="ts">
import { computed } from 'vue';
import RecordLink from '@/components/RecordLink.vue';
import ZoneDisplayString from '@/components/ZoneDisplayString.vue';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { useQuickSearchMatches } from '@/composables/quickSearchMatches';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';
import { useZoneIcon } from '@/composables/useZoneIcon';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { readNestedDisplayString, readNestedId } from '@/utils/nestedRelation';
import {
    readZoneDisplayString,
    resolveZoneLocation,
    splitZoneDisplayString,
} from '@/utils/zoneDisplayString';

/**
 * "Where it lives" — line two of a bucket or custom list, on the search card
 * and in the inspector heading alike: the zone mark, then the zone's display
 * string with its location half and zone half linked separately.
 *
 * Renders nothing at all when the row carries no zone. A bucket without a zone
 * is a vault, not an error, so there is no "Unknown zone" fallback here.
 *
 * When the zone's name cannot be split against its location, the location is
 * printed here as its own link — the same rule the player card applies with
 * headingCarriesLocation. That matters more on this surface: the container
 * cards pin the Location column off the summary line AND out of the
 * inspector's Details, so this line is the only place left that shows it.
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        /**
         * The quick-search fields this row matched (SAU-591), passed in by the
         * search card that draws this line. This line never reads the page's
         * matches by row id itself. It is drawn in about ten places, including
         * other zones' lines inside the same custom list card, and those must
         * never highlight this row's match.
         */
        matchedFields?: readonly string[];
        /**
         * How many lines a long zone takes before it clips. The bucket and
         * custom list cards and their inspector heading pass 2. Every other
         * caller keeps the one clipped line.
         */
        maxLines?: 1 | 2;
    }>(),
    { matchedFields: () => [], maxLines: 1 }
);

const zoneDisplay = computed(() => readZoneDisplayString(props.row.location_zone));

const locationRef = computed(() => resolveZoneLocation(props.row.location_zone, props.row.location));
const locationName = computed(() => readNestedDisplayString(locationRef.value));
const locationId = computed(() => readNestedId(locationRef.value));

const headingCarriesLocation = computed(
    () => splitZoneDisplayString(zoneDisplay.value, locationName.value).location !== null
);

/** Shown only when the zone's own name does not already carry the location. */
const trailingLocation = computed(() =>
    headingCarriesLocation.value ? null : locationName.value
);

/** The whole line, for the tooltip, because it clips. */
const lineTitle = computed(() =>
    trailingLocation.value === null
        ? (zoneDisplay.value ?? undefined)
        : `${zoneDisplay.value} · ${trailingLocation.value}`
);

/**
 * Whether this line draws one of the row's matched fields. The location counts
 * only when the line draws the row's own location: on a master zone it draws
 * the master's. `cardShownMatchFields` applies the same rule and must stay in
 * step with it.
 */
const holdsMatch = computed(() => {
    if (props.matchedFields.includes('location_zone.display_string')) {
        return true;
    }
    return (
        props.matchedFields.includes('location.display_string') &&
        locationName.value !== null &&
        locationName.value === readNestedDisplayString(props.row.location)
    );
});
const quickSearch = useQuickSearchMatches();
const highlightTerms = computed(() => quickSearch?.terms.value ?? []);

const { canOpen } = useOpenRecordInInspector();
const locationInspectable = computed(() => locationId.value !== null && canOpen('Location'));

// Master, subscriber or plain zone, by the shared rule (see useZoneIcon).
const zoneIcon = useZoneIcon(() => props.row.location_zone);
</script>

<template>
    <p
        v-if="zoneDisplay !== null"
        class="text-muted-foreground flex min-w-0 items-center gap-1 leading-snug"
        :title="lineTitle"
        data-testid="container-zone-line"
    >
        <component
            :is="zoneIcon"
            v-if="zoneIcon"
            class="size-3.5 shrink-0"
            aria-hidden="true"
            data-testid="container-zone-icon"
        />
        <!-- While it holds a match the line wraps instead of clipping, so the
             highlight is never hidden behind the ellipsis. -->
        <span
            class="min-w-0"
            :class="holdsMatch ? 'wrap-anywhere' : maxLines === 2 ? 'line-clamp-2' : 'truncate'"
            data-testid="container-zone-line-text"
        >
            <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="holdsMatch">
                <ZoneDisplayString :zone="row.location_zone" :location="row.location" />
                <template v-if="trailingLocation !== null">
                    {{ ' · ' }}
                    <!--
                      A link to the location's page, with the side-peek button on
                      hover. It uses RecordLink's `inline` mode, which is load-bearing:
                      an inline-flex box cannot be clipped by text-overflow, so in this
                      truncating line it would drop the whole location rather than end
                      it with "…".
                    -->
                    <RecordLink
                        v-if="locationInspectable"
                        entity-id="Location"
                        :record-id="locationId!"
                        :label="`location ${trailingLocation}`"
                        inline
                        data-testid="container-location-inspect"
                        >{{ trailingLocation }}</RecordLink
                    >
                    <template v-else>{{ trailingLocation }}</template>
                </template>
            </SearchHighlight>
        </span>
    </p>
</template>
